import { useEffect } from 'react';
import { useQueryClient } from '@tanstack/react-query';

import { useGetConfigByKey } from '@/entities/regional-configuration/hooks/useGetConfigByKey';
import { RESTART_INFO_UPDATE, TrackingCategory } from '@/entities/tracking/const';
import { TrackerEngine } from '@/entities/tracking/lib/tracker-engine';
import { platformRestartMutationKey } from '@/utils/platformRestart';

import { signOutFlowMutationKey } from '../signout/useSignOutFlow/useSignOutFlow';

const TRACKED_PLATFORM_RESTART_MUTATION_KEYS = new Set([
    signOutFlowMutationKey,
    platformRestartMutationKey,
]);

type PlatformRestartData = { deviceId?: string; session?: { id?: string } } | undefined;

// Omit identity when the restart response carries no deviceId — an `fpid: undefined`
// update degrades the fpid dataHub already holds, and the AEP interact datastream
// rejects events without FPID (EXEG-0103-400).
const buildRestartDetail = (data: PlatformRestartData) => ({
    ...(data?.deviceId && { identity: { fpid: data.deviceId } }),
    appInfo: {
        stitcherSessionId: data?.session?.id,
    },
});

export const useTrackPlatformRestart = (): void => {
    const shouldTrack = useGetConfigByKey<boolean>(
        'options.trackingConfig.shouldTrack.restartinfoupdate',
    );
    const queryClient = useQueryClient();

    useEffect(() => {
        if (!queryClient || !shouldTrack) return;

        const mutationCache = queryClient.getMutationCache();
        const unsubscribe = mutationCache.subscribe((event) => {
            const mutation = event?.mutation;
            const key = mutation?.options.mutationKey?.[0];
            if (!TRACKED_PLATFORM_RESTART_MUTATION_KEYS.has(key as string)) return;

            if (event.type !== 'updated' || mutation?.state?.status !== 'success') return;

            void TrackerEngine.update({
                type: RESTART_INFO_UPDATE,
                detail: buildRestartDetail(mutation?.state?.data?.platformRestart),
                category: TrackingCategory.App.OPERATION,
            });
        });

        return () => unsubscribe();
    }, [queryClient, shouldTrack]);
};
